import {ParamMap, Router} from '@angular/router';
import {Feature} from '@catcore/services/feature/feature';
import {GenericLinkedResource, JsonSchemaEntityRef} from '@catcore/types/base';
import {typeLabelsMap, typeLabelsMap as labelMap} from '@catcore/utils/json-schema';
import {NodeQueryParamInfo} from '@catfeatures/types';
import {ExplorerItem, LinkItem, TreeNodeInfo} from '@catui/types';
import {isNullOrUndefined} from 'util';
import {RoutingsForFeatureEntities} from './routing-command-info';

/**
 * Additional information for creating a ExplorerItem through a factory
 */
interface ExplorerItemFactoryOption {
  data: any[];
  type: string;
  command: string;
  allowedActions: any[];
  description?: string;
  filter?: string[];
}

/**
 * Name for the query param which conatains state information about a single Feature page view
 * @type {string}
 */
const PARAM_FEATURE_LIST_STATE = 'pfls';

/**
 * Returns a representation for addressing a specific resource component from a Tree Node, that can be used as a model to
 * create url query parameters.
 *
 * @param {TreeNodeInfo} nodeInfo
 * @returns {NodeQueryParamInfo}
 */
export const buildQueryParamsForTargetNode = (nodeInfo: TreeNodeInfo): NodeQueryParamInfo => {
  const queryParams: NodeQueryParamInfo = {};
  const allowBuild = !isNullOrUndefined(nodeInfo.item.value.route.resourceId);

  if (allowBuild && nodeInfo.item.value.route.resourceId) {
    queryParams.resource = nodeInfo.item.value.route.resourceId;
  }
  if (allowBuild && nodeInfo.item.value.route.parentResourceId) {
    queryParams.parentResourceId = nodeInfo.item.value.route.parentResourceId;
  }
  return queryParams;
};

/**
 * Try to determine the correct resource name for a given url endpoint
 */
export const normalizeResourceToKey = (resourceId: string): string => {
  let key = null;
  const elements = resourceId ? resourceId.split('/') : [];

  do {
    key = elements.pop();
  } while (elements.length > 0 && !key.match(/[a-zA-Z\-\_]+/));

  return key;

};

/**
 * The function returns a computed label for a given valid resourceId.
 * Currently only endpoints that adressing collections are supported.
 *
 * Example:
 * Given a id of '/api/features/1/ggw-devices' the label would be 'Ggw Devices'
 *
 * @param {string} resourceId
 * @returns {string}
 */
export const labelForResourceId = (resourceId: string): string => {
  return resolveLabelForElementName(normalizeResourceToKey(resourceId).replace(/\-/g, '_'), false);
};

/**
 * Determines and returns a label for a Resource endpoint based on a given reference.
 * @param childName
 * @param isMany
 * @returns {string}
 */
export const resolveLabelForElementName = (childName, isMany): string => {
  let nameMap = null;
  let returnable = childName;

  typeLabelsMap.forEach((labelDef) => {
    if (labelDef.matchingPropertyNames.includes(childName)) {
      nameMap = labelDef;
    }
  });

  if (nameMap && isMany) {
    returnable = nameMap.many;
  }

  if (nameMap && !isMany) {
    returnable = nameMap.one;
  }

  return returnable;
};

/**
 * Returns a factory to create a new ExplorerItem with similar base configurations
 * @param treeNodeFactory
 * @param defaultFactoryOptions
 * @returns {(options: ExplorerItemFactoryOption) => ExplorerItem}
 */
export const itemFactory = (treeNodeFactory: any, defaultFactoryOptions: any) => {
  return (options: ExplorerItemFactoryOption): ExplorerItem => {
    const labels = labelMap.get(options.type);
    return {
      label: labels ? labels.many : options.type,
      expanded: false,
      value:
        {
          countItems: options.data.length,
          route:
            {
              command: options.command,
            },
        },
      children: treeNodeFactory.getForRoot(
        options.data,
        {
          label: () => labels ? labels.one : options.type,
          ...defaultFactoryOptions,
        },
        options.filter,
      ),
      allowedActions: options.allowedActions,
    };
  };
};

/**
 * Given a list with entity schema information, the function returns a list of possible related routing
 * navigation commands for every found entity.
 *
 * @param items
 * @param {Feature} feature
 * @returns {LinkItem[]}
 */
export const buildRouteCommandsForEntities = (items: JsonSchemaEntityRef[], feature: Feature): LinkItem[] => {
  const routeCommands = [];
  const entityListNames = Object.keys(RoutingsForFeatureEntities);
  items.forEach((item) => {
    let routeRef = null;

    if (entityListNames.includes(item.entityRef)) {
      routeRef = RoutingsForFeatureEntities[item.entityRef];
    }

    if (routeRef) {
      routeRef.forEach((routeInfo) => {
        routeCommands.push({
          label: routeInfo.label,
          navigate: (router) => {
            router.navigate(routeInfo.provide(feature));
          },
        });
      });
    }
  });
  return routeCommands;

};

/**
 * Creates a QueryParam object for a Feature page view containing a list of query params in a base64 state
 * @param queryParams
 * @returns {any}
 */
export const serializeFeatureParams = (queryParams: any): any => {
  const params = {};
  params[PARAM_FEATURE_LIST_STATE] = btoa(JSON.stringify(queryParams));
  return params;
};

/**
 * Returns a readable version of query parameters for a Feature page view
 * @param {ParamMap} params
 * @returns {any}
 */
export const deserializeParamsForFeature = (params: ParamMap): any => {
  return deserializeParamsFor(PARAM_FEATURE_LIST_STATE, params);
};
export const deserializeParamsFor = (key: string, params: ParamMap): any => {
  const encodedValue = params.get(key);
  const decodedValue = encodedValue ? atob(encodedValue) : null;
  return JSON.parse(decodedValue);
};

/**
 * Shortcut function to navigate to a Resource
 *
 * @param {Router} router
 * @param {Feature} feature
 * @param {GenericLinkedResource} item
 */
export const navigateToFeatureResource = (router: Router, feature: Feature, item: GenericLinkedResource) => {
  const queryParams = {resource: item.href};
  router.navigate(['features', feature.id, 'resource'], {queryParams});
};



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/features/shared/utils.ts